import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../AdminAuthContext";
import {
  PageHeader, Badge, Table, TextInput, Select, Panel,
} from "../components/UIKit";
import { Search } from "lucide-react";

export default function AdminUsers() {
  const { api } = useAdminAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      api("/users", { params: { q: q || undefined, status } })
        .then((d) => {
          setItems(d.items || []);
          setTotal(d.total || 0);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, status, api]);

  const rows = useMemo(
    () =>
      items.map((u) => ({
        ...u,
        full_name: `${u.first_name} ${u.last_name}`,
        devices_count: (u.mattresses || []).length,
        primary_device: u.mattresses?.[0]?.label || "—",
        primary_device_id: u.mattresses?.[0]?.id || "—",
      })),
    [items]
  );

  return (
    <div className="p-8">
      <PageHeader
        title="Utenti & Dispositivi"
        subtitle={`${total} utenti registrati`}
      />

      <div className="flex items-center gap-2 mb-4">
        <TextInput
          icon={<Search size={14} />}
          placeholder="Cerca per nome o email…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select
          value={status}
          onChange={setStatus}
          options={[
            { value: "all", label: "Tutti gli stati" },
            { value: "online", label: "Online" },
            { value: "offline", label: "Offline" },
            { value: "stale", label: "Inattivo" },
          ]}
        />
        {loading && <span className="text-white/50 text-[12px]">Caricamento…</span>}
      </div>

      <Table
        columns={[
          {
            key: "full_name",
            label: "Utente",
            render: (r) => (
              <div>
                <div className="text-white">{r.full_name}</div>
                <div className="text-white/55 text-[11px]">{r.email}</div>
              </div>
            ),
          },
          {
            key: "primary_device",
            label: "Dispositivo",
            render: (r) => (
              <div>
                <div className="text-white/90 text-[13px]">{r.primary_device}</div>
                <div className="text-white/50 text-[11px] font-mono">
                  {r.primary_device_id}
                </div>
              </div>
            ),
          },
          {
            key: "status",
            label: "Stato",
            render: (r) => (
              <Badge
                color={
                  r.status === "online"
                    ? "teal"
                    : r.status === "stale"
                    ? "amber"
                    : "slate"
                }
              >
                {r.status}
              </Badge>
            ),
          },
          {
            key: "last_session_at",
            label: "Ultima sessione",
            render: (r) =>
              r.last_session_at
                ? new Date(r.last_session_at).toLocaleString("it-IT", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—",
          },
          {
            key: "total_sessions",
            label: "Sessioni",
            render: (r) => (
              <span className="font-mono text-white">{r.total_sessions}</span>
            ),
          },
          {
            key: "quality_flag_pct",
            label: "Qualità 14gg",
            render: (r) => (
              <div className="flex items-center gap-2">
                <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className={`h-full ${
                      r.quality_flag_pct >= 80
                        ? "bg-[#14c2a1]"
                        : r.quality_flag_pct >= 50
                        ? "bg-amber-400"
                        : "bg-rose-400"
                    }`}
                    style={{ width: `${r.quality_flag_pct}%` }}
                  />
                </div>
                <span className="text-white/85 text-[12px] w-10">
                  {r.quality_flag_pct}%
                </span>
              </div>
            ),
          },
        ]}
        rows={rows}
        onRowClick={(r) => nav(`/admin/users/${r.id}`)}
      />
    </div>
  );
}
